import React from 'react';
import { isEqual } from 'lodash';

import {
  Company,
  CompanyEmployee,
  useRefresh,
  MarketCode,
  RoleUser,
  RoleEmployee
} from '@homesusa/core';
import { useUserServices } from '../../hooks/use-user-services';
import { AuthCookiesManager } from '../../utils/cookies-manager';
import { Loading } from '../../components/loading.component';
import { useAuth } from '../auth';
import { AuthCompanyContext } from './company.context';

interface CurrentInformation {
  market: MarketCode;
  company?: Company;
}

export function AuthCompanyProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const auth = useAuth();
  const { getCompanies, getEmployee } = useUserServices();
  const [currentInfo, setCurrentInfo] = React.useState<CurrentInformation>(
    AuthCookiesManager.getAuthCookie()
  );
  const [currentEmployee, setEmployee] = React.useState<CompanyEmployee | undefined>(
    AuthCookiesManager.getAuthCookie().companyEmployee
  );
  const [userCompanies, setUserCompanies] = React.useState<Company[] | undefined>(
    AuthCookiesManager.getCompaniesCookie()?.companies
  );
  const [refreshing, startRefreshing] = useRefresh();

  const removeCurrentCompany = (): void => {
    if (currentInfo.company === undefined) {
      return;
    }

    AuthCookiesManager.removeCompany();
    setCurrentInfo((prev) => ({
      ...prev,
      company: undefined
    }));
    setEmployee(undefined);
  };

  const setLocalCompany = async (company: Company): Promise<void> => {
    if (!auth.user || currentInfo.company?.id === company.id) {
      return;
    }

    AuthCookiesManager.setCompany(company);
    setCurrentInfo({
      market: company.marketCode,
      company
    });

    const employee =
      company && auth.user.role === RoleUser.User ? await getEmployee(company.id) : undefined;
    setLocalCompanyEmployee(employee);

    startRefreshing();
  };

  // set employee as mls admin
  const setEmployeeRole = async (employeeRole?: RoleEmployee): Promise<void> => {
    if (
      auth.user == null ||
      auth.user.role != RoleUser.MLSAdministrator ||
      currentInfo.company == null
    ) {
      return;
    }

    const { email, firstName, lastName, id: userId } = auth.user;
    setLocalCompanyEmployee(
      employeeRole != null
        ? {
            userId,
            lastName,
            firstName,
            email,
            title: RoleUser.MLSAdministrator,
            id: `${RoleUser.MLSAdministrator}-${employeeRole}-${userId}`,
            companyName: currentInfo.company.name,
            roleName: employeeRole,
            userName: email,
            sysCreatedOn: new Date()
          }
        : undefined
    );

    startRefreshing();
  };

  const setLocalUserCompanies = async (availableCompanies: Company[]): Promise<void> => {
    if (!auth.user || (userCompanies !== undefined && isEqual(userCompanies, availableCompanies))) {
      return;
    }

    AuthCookiesManager.setUserCompanies(availableCompanies);
    setUserCompanies(availableCompanies);
  };

  const setLocalCompanyEmployee = async (employee?: CompanyEmployee): Promise<void> => {
    if (!auth.user || currentEmployee?.id === employee?.id) {
      return;
    }

    AuthCookiesManager.setCompanyEmployee(employee);
    setEmployee(employee);
  };

  const setLocalMarket = async (marketCode: MarketCode): Promise<void> => {
    AuthCookiesManager.removeCompany();
    AuthCookiesManager.setMarket(marketCode);
    setCurrentInfo({
      market: marketCode,
      company: undefined
    });
    setLocalCompanyEmployee(undefined);

    startRefreshing();
  };

  const setCurrentCompany = async (
    newCompany?: Company | null,
    marketCode?: MarketCode
  ): Promise<void> => {
    if (newCompany) {
      await setLocalCompany(newCompany);
    } else if (marketCode) {
      await setLocalMarket(marketCode);
    }
  };

  React.useLayoutEffect(() => {
    getUserCompanies();
  }, [auth.user]);

  const getUserCompanies = async (): Promise<void> => {
    if (!auth.user) {
      return;
    }

    const availableCompanies = await getCompanies(false);
    const company = currentInfo.company ?? AuthCookiesManager.getAuthCookie().company;

    const isValidCompany = (currentCompany: Company): boolean => {
      return availableCompanies.some((company) => company.id === currentCompany.id);
    };

    if (availableCompanies.length < 1 || (company && !isValidCompany(company))) {
      removeCurrentCompany();
    }

    if (auth.user.role !== RoleUser.User) {
      setLocalUserCompanies(availableCompanies);
      return;
    }

    setCurrentCompany(company ?? availableCompanies[0]);
    setLocalUserCompanies(availableCompanies);
  };

  return (
    <AuthCompanyContext.Provider
      value={{
        currentMarket: currentInfo.market,
        currentEmployee,
        currentCompany: currentInfo.company,
        userCompanies,
        setCurrentCompany,
        refreshing: userCompanies === undefined || refreshing,
        disabled: false,
        setEmployeeRole
      }}
    >
      {auth?.user?.role === RoleUser.User && currentInfo.company == null ? <Loading /> : children}
    </AuthCompanyContext.Provider>
  );
}
